/**
 * Grid Item Layout Presets — Single Source of Truth
 *
 * Default values for ALL settings applied when switching grid item layout type.
 * After switching, user can customize any setting through UI.
 * No hardcoded values in renderers — everything comes from data.
 *
 * Rules:
 * - imageContainer + textContainer heights always sum to 100%
 * - All paddings, scale modes, dimensions come from presets → written to data → read by renderer
 * - Presets are applied via applyGridPreset() on layout switch
 */

// ─── Preset Interfaces ───

export interface ContainerPreset {
  heightValue: number;
  verticalAlignment: string;
  contentAlignment?: string;
  insets?: { top: number; end: number; bottom: number; start: number };
}

export interface ImageDefaults {
  /** Scale mode for image: 'scaleAspectFill' | 'scaleAspectFit' */
  scaleMode: string;
  /** Explicit image dimensions (null = not set, image fills container) */
  dimensions?: { width: number; height: number };
  /** Image padding (layout.insets) */
  padding?: { top: number; end: number; bottom: number; start: number };
}

export interface GridItemLayoutPreset {
  /** Image container settings (null for text-only layouts) */
  imageContainer?: ContainerPreset;
  /** Text container settings (null for text-only layouts) */
  textContainer?: ContainerPreset;
  /** Default item paddings (appearance.insets) */
  defaultItemInsets: { top: number; end: number; bottom: number; start: number };
  /** Default cell height as % of width (null = auto/content-based for text-only) */
  cellHeightFromCellWidth?: number;
  /** Default image appearance per item */
  imageDefaults?: ImageDefaults;
}

// ─── All 6 Grid Item Layout Presets ───
// Image + Text container heights always sum to 100%

export const GRID_ITEM_LAYOUT_PRESETS: Record<string, GridItemLayoutPreset> = {
  // Text-only layouts — no containers, just padded text
  Title: {
    defaultItemInsets: { top: 12, end: 12, bottom: 12, start: 12 },
  },
  TitleSubtitle: {
    defaultItemInsets: { top: 12, end: 12, bottom: 12, start: 12 },
  },

  // 50/50 — small image + text below, shorter cell
  SmallImageTitle: {
    cellHeightFromCellWidth: 60,
    imageContainer: { heightValue: 50, verticalAlignment: 'top', contentAlignment: 'top' },
    textContainer: {
      heightValue: 50,
      verticalAlignment: 'bottom',
      contentAlignment: 'bottom',
      insets: { top: 0, end: 0, bottom: 12, start: 0 },
    },
    defaultItemInsets: { top: 0, end: 0, bottom: 0, start: 0 },
    imageDefaults: {
      scaleMode: 'scaleAspectFit',
      dimensions: { width: 50, height: 50 },
      padding: { top: 8, end: 0, bottom: 0, start: 0 },
    },
  },

  // 60/40 — medium image fills top, text below with padding
  MediumImageTitle: {
    cellHeightFromCellWidth: 100,
    imageContainer: { heightValue: 60, verticalAlignment: 'top' },
    textContainer: {
      heightValue: 40,
      verticalAlignment: 'bottom',
      contentAlignment: 'center',
      insets: { top: 16, end: 16, bottom: 16, start: 16 },
    },
    defaultItemInsets: { top: 0, end: 0, bottom: 0, start: 0 },
    imageDefaults: { scaleMode: 'scaleAspectFill' },
  },

  // 70/30 — large image, small text at bottom
  BigImageTitle: {
    cellHeightFromCellWidth: 100,
    imageContainer: { heightValue: 70, verticalAlignment: 'top' },
    textContainer: {
      heightValue: 30,
      verticalAlignment: 'bottom',
      contentAlignment: 'top',
      insets: { top: 16, end: 16, bottom: 16, start: 16 },
    },
    defaultItemInsets: { top: 0, end: 0, bottom: 0, start: 0 },
    imageDefaults: { scaleMode: 'scaleAspectFill' },
  },

  // 100/100 — full-bleed image, text overlay at bottom
  FullImage: {
    cellHeightFromCellWidth: 100,
    imageContainer: { heightValue: 100, verticalAlignment: 'top' },
    textContainer: { heightValue: 100, verticalAlignment: 'bottom', contentAlignment: 'bottom' },
    defaultItemInsets: { top: 0, end: 0, bottom: 0, start: 0 },
    imageDefaults: { scaleMode: 'scaleAspectFill' },
  },
};

// ─── Converter: ContainerPreset → render-side defaults ───

/**
 * Render-side container defaults (same shape as ContainerDefaults from containerStyles.ts).
 * Defined here to avoid cross-module dependency (reactFlow → screen-view-v2).
 */
export interface GridContainerDefaults {
  height: number;
  vAlign: string;
  contentAlign?: string;
}

/**
 * Convert editor-side ContainerPreset to render-side ContainerDefaults.
 */
export function toContainerDefaults(preset: ContainerPreset): GridContainerDefaults {
  return {
    height: preset.heightValue,
    vAlign: preset.verticalAlignment,
    ...(preset.contentAlignment != null && { contentAlign: preset.contentAlignment }),
  };
}

// ─── Visibility Table ───

export interface GridLayoutVisibility {
  showImage: boolean;
  showSubtitle: boolean;
  showCheckbox: boolean;
  showImageContainer: boolean;
  showTextContainer: boolean;
  showCheckboxContainer: boolean;
  showCellDimensions: boolean;
  showImageStyles: boolean;
  showSubtitleStyles: boolean;
}

export const GRID_LAYOUT_VISIBILITY: Record<string, GridLayoutVisibility> = {
  Title: {
    showImage: false, showSubtitle: false, showCheckbox: false,
    showImageContainer: false, showTextContainer: false, showCheckboxContainer: false,
    showCellDimensions: false, showImageStyles: false, showSubtitleStyles: false,
  },
  TitleSubtitle: {
    showImage: false, showSubtitle: true, showCheckbox: false,
    showImageContainer: false, showTextContainer: false, showCheckboxContainer: false,
    showCellDimensions: false, showImageStyles: false, showSubtitleStyles: true,
  },
  SmallImageTitle: {
    showImage: true, showSubtitle: false, showCheckbox: false,
    showImageContainer: true, showTextContainer: true, showCheckboxContainer: true,
    showCellDimensions: true, showImageStyles: true, showSubtitleStyles: false,
  },
  MediumImageTitle: {
    showImage: true, showSubtitle: false, showCheckbox: false,
    showImageContainer: true, showTextContainer: true, showCheckboxContainer: true,
    showCellDimensions: true, showImageStyles: true, showSubtitleStyles: false,
  },
  BigImageTitle: {
    showImage: true, showSubtitle: false, showCheckbox: false,
    showImageContainer: true, showTextContainer: true, showCheckboxContainer: true,
    showCellDimensions: true, showImageStyles: true, showSubtitleStyles: false,
  },
  FullImage: {
    showImage: true, showSubtitle: false, showCheckbox: false,
    showImageContainer: true, showTextContainer: true, showCheckboxContainer: true,
    showCellDimensions: true, showImageStyles: true, showSubtitleStyles: false,
  },
};

// ─── Apply Preset ───

/**
 * Apply a grid layout preset via a single merged onChange call.
 * Writes ALL preset values to data — containers, item paddings, image defaults.
 * Clears stale appearance.insets from title/subtitle (hidden padding bug).
 */
export function applyGridPreset(
  onChange: (path: string, value: unknown) => void,
  presetKey: string,
  currentOptions: Record<string, unknown>,
): void {
  const preset = GRID_ITEM_LAYOUT_PRESETS[presetKey];
  if (!preset) return;

  // Clear stale appearance.insets from items (hidden padding not controlled by UI)
  // + apply image defaults from preset
  const items = currentOptions?.items;
  const cleanedItems = Array.isArray(items)
    ? items.map((item: Record<string, unknown>) => {
        const cleaned = { ...item };
        // Strip hidden appearance.insets from title/subtitle
        for (const field of ['title', 'subtitle'] as const) {
          const label = cleaned[field] as Record<string, unknown> | undefined;
          if (label?.appearance && (label.appearance as Record<string, unknown>).insets) {
            const { insets: _, ...restAppearance } = label.appearance as Record<string, unknown>;
            cleaned[field] = { ...label, appearance: restAppearance };
          }
        }
        // Apply image defaults from preset
        if (preset.imageDefaults) {
          const currentImage = (cleaned.image ?? {}) as Record<string, unknown>;
          const currentImgAppearance = (currentImage.appearance ?? {}) as Record<string, unknown>;
          const currentImgLayout = (currentImage.layout ?? {}) as Record<string, unknown>;
          cleaned.image = {
            ...currentImage,
            appearance: {
              ...currentImgAppearance,
              scaleMode: preset.imageDefaults.scaleMode,
              ...(preset.imageDefaults.dimensions
                ? { dimensions: { ...preset.imageDefaults.dimensions } }
                : { dimensions: null }),
            },
            layout: {
              ...currentImgLayout,
              insets: preset.imageDefaults.padding ?? null,
            },
          };
        }
        return cleaned;
      })
    : items;

  const currentAppearance = (currentOptions?.appearance as Record<string, unknown>) ?? {};

  // Text-only layouts — no containers, remove cellHeightFromCellWidth (height by content)
  if (!preset.imageContainer) {
    onChange('content.options', {
      ...currentOptions,
      itemLayout: presetKey,
      cellHeightFromCellWidth: null,
      items: cleanedItems ?? items,
      appearance: { ...currentAppearance, insets: preset.defaultItemInsets },
    });
    return;
  }

  // Image-based layouts — set containers + item paddings + image defaults
  const imageContainer = {
    heightKind: 'percent',
    heightValue: preset.imageContainer.heightValue,
    verticalAlignment: preset.imageContainer.verticalAlignment,
    contentAlignment: preset.imageContainer.contentAlignment ?? null,
    insets: preset.imageContainer.insets ?? null,
  };

  const textContainer = preset.textContainer
    ? {
        heightKind: 'percent',
        heightValue: preset.textContainer.heightValue,
        verticalAlignment: preset.textContainer.verticalAlignment,
        contentAlignment: preset.textContainer.contentAlignment ?? null,
        insets: preset.textContainer.insets ?? null,
      }
    : null;

  onChange('content.options', {
    ...currentOptions,
    itemLayout: presetKey,
    cellHeightFromCellWidth: preset.cellHeightFromCellWidth ?? 100,
    items: cleanedItems ?? items,
    appearance: {
      ...currentAppearance,
      imageContainer,
      textContainer,
      insets: preset.defaultItemInsets,
    },
  });
}
